/**
 * Admin Users List Page
 *
 * Paginated table of all platform users with search, filters and quick
 * actions. Filters (search, plan, status, join-date range) are reflected in
 * the URL query string, so a filtered view can be copied and shared.
 */

import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  CaretRight as ChevronRight,
  FileText,
  EnvelopeOpen as MailCheck,
  EnvelopeSimple as MailX,
  ArrowsClockwise as RefreshCw,
  Shield,
  UsersThree as Users,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import {
  Alert,
  EmptyState,
  HeaderAction,
  PageHeader,
  type PillTone,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@charisol-spark/ui";
import { Button } from "@/components/ui/Button";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import { useLoadMore } from "@/components/lists/useLoadMore";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminUsers } from "@/hooks/useAdmin";
import type { AdminUserSummary } from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

const PLAN_TONES: Record<string, PillTone> = {
  free: "neutral",
  pro: "violet",
  team: "blue",
  enterprise: "amber",
};

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "createdAt", label: "Signed up" },
  { field: "lastLoginAt", label: "Last login" },
  { field: "name", label: "Name", kind: "text" },
];

const PLAN_OPTIONS = [
  { value: "", label: "All plans" },
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro" },
  { value: "team", label: "Team" },
  { value: "enterprise", label: "Enterprise" },
] as const;

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
] as const;

/** Newest sign-ups first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  searchKey: "search",
  filterKeys: ["plan", "status", "createdFrom", "createdTo"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "createdAt", dir: "desc" },
};

const SORT_GETTERS: Record<
  string,
  (user: AdminUserSummary) => string | undefined
> = {
  createdAt: (u) => u.createdAt,
  lastLoginAt: (u) => u.lastLoginAt,
  name: (u) => (u.name || u.email).toLowerCase(),
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Empty string reads as "no filter" everywhere below; `undefined` is what the API wants. */
function orUndefined(value: string): string | undefined {
  return value ? value : undefined;
}

export function AdminUsersPage() {
  const navigate = useNavigate();
  const list = useListUrlState(URL_CONFIG);
  const { filters } = list;

  const pager = useLoadMore<AdminUserSummary>(
    JSON.stringify([list.search, filters])
  );

  const { data, isLoading, error, refetch, isFetching } = useAdminUsers({
    search: orUndefined(list.search),
    plan: orUndefined(filters.plan!),
    status: orUndefined(filters.status!) as "active" | "disabled" | undefined,
    createdFrom: orUndefined(filters.createdFrom!),
    createdTo: orUndefined(filters.createdTo!),
    cursor: pager.cursor,
    limit: 25,
  });

  const displayUsers = useMemo(
    () =>
      sortRows(
        [...pager.previous, ...(data?.users ?? [])],
        list.sort,
        SORT_GETTERS
      ),
    [pager.previous, data?.users, list.sort]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        variant="classic"
        title="Users"
        description="Manage platform user accounts."
        action={
          <HeaderAction
            icon={
              <RefreshCw
                className={cn("h-4 w-4", isFetching && "animate-spin")}
              />
            }
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </HeaderAction>
        }
      />

      <AdminListToolbar
        label="Users"
        search={{
          label: "Search users",
          value: list.search,
          onChange: list.setSearch,
          placeholder: "Search by email or name...",
        }}
        filters={[
          {
            id: "plan",
            label: "Plan",
            kind: "select",
            options: PLAN_OPTIONS,
            value: filters.plan!,
            onChange: (v) => list.setFilter("plan", v),
          },
          {
            id: "status",
            label: "Status",
            kind: "select",
            options: STATUS_OPTIONS,
            value: filters.status!,
            onChange: (v) => list.setFilter("status", v),
          },
          {
            id: "createdFrom",
            label: "Joined from",
            kind: "date",
            value: filters.createdFrom!,
            max: filters.createdTo || undefined,
            onChange: (v) => list.setFilter("createdFrom", v),
          },
          {
            id: "createdTo",
            label: "Joined to",
            kind: "date",
            value: filters.createdTo!,
            min: filters.createdFrom || undefined,
            onChange: (v) => list.setFilter("createdTo", v),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "page",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {/* Table */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {getErrorMessage(error, "Failed to load users.")}
        </Alert>
      ) : (
        <>
          {data?.truncated && (
            <p
              role="status"
              className="flex items-center gap-2 rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-600/30 dark:bg-amber-900/10 dark:text-amber-200"
            >
              <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Showing partial results — the scan stopped early. Load more to
              keep going.
            </p>
          )}

          <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
            {displayUsers.length === 0 ? (
              <EmptyState
                icon={Users}
                title={
                  list.hasActive
                    ? "No users match your filters."
                    : "No users found."
                }
              />
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>User</TH>
                    <TH className="hidden sm:table-cell">Plan</TH>
                    <TH className="hidden md:table-cell">Posts</TH>
                    <TH className="hidden md:table-cell">Verified</TH>
                    <TH className="hidden lg:table-cell">Signed Up</TH>
                    <TH className="hidden xl:table-cell">Last Login</TH>
                    <TH>Access</TH>
                    <TH className="hidden md:table-cell">Reports</TH>
                    <TH className="w-10" />
                  </tr>
                </THead>
                <TBody>
                  {displayUsers.map((user) => (
                    <TR
                      key={user.userId}
                      role="link"
                      tabIndex={0}
                      className="cursor-pointer focus-visible:bg-accent/30 focus-visible:outline-none"
                      onClick={() => navigate(`/users/${user.userId}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/users/${user.userId}`);
                        }
                      }}
                    >
                      <TD>
                        <p className="flex items-center gap-1.5 font-medium">
                          {user.name || user.email}
                          {user.roles.length > 0 && (
                            <StatusPill
                              tone="violet"
                              className="gap-0.5 px-1.5"
                            >
                              <Shield className="h-2.5 w-2.5" />
                              {user.roles[0]?.replace("platform_", "")}
                            </StatusPill>
                          )}
                        </p>
                        {user.name && (
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        )}
                      </TD>
                      <TD className="hidden sm:table-cell">
                        <StatusPill tone={PLAN_TONES[user.plan] ?? "neutral"}>
                          {user.plan}
                        </StatusPill>
                      </TD>
                      <TD className="hidden md:table-cell">
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <FileText className="h-3.5 w-3.5" />
                          {user.totalPublishedPosts}
                        </span>
                      </TD>
                      <TD className="hidden md:table-cell">
                        {user.emailVerified ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <MailCheck className="h-3.5 w-3.5" />
                            <span className="text-xs">Yes</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-500">
                            <MailX className="h-3.5 w-3.5" />
                            <span className="text-xs">No</span>
                          </span>
                        )}
                      </TD>
                      <TD className="hidden text-muted-foreground lg:table-cell">
                        {formatDate(user.createdAt)}
                      </TD>
                      <TD className="hidden text-muted-foreground xl:table-cell">
                        {formatDate(user.lastLoginAt)}
                      </TD>
                      <TD>
                        {user.isActive ? (
                          <StatusPill tone="green" className="gap-1">
                            <CheckCircle className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Enabled</span>
                          </StatusPill>
                        ) : (
                          <StatusPill tone="red" className="gap-1">
                            <XCircle className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Disabled</span>
                          </StatusPill>
                        )}
                      </TD>
                      <TD className="hidden md:table-cell">
                        <Link
                          to={`/reports?user=${encodeURIComponent(user.userId)}`}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="text-xs text-primary hover:underline"
                          aria-label={`Reports for ${user.name || user.email}`}
                        >
                          Reports
                        </Link>
                      </TD>
                      <TD>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {data?.cursor && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                pill
                onClick={() => pager.loadMore(data?.users ?? [], data?.cursor)}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
